import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
// @mui
import { Box, Stack, Drawer } from "@mui/material";
// hooks
import useResponsive from "../../../../../minimals/hooks/useResponsive";
// config
import { NAV } from "../../../../../minimals/config-global";
// components
import Logo from "../../../../../minimals/components/logo";
import Scrollbar from "../../../../../minimals/components/scrollbar";
import { NavSectionVertical } from "../../../../../minimals/components/nav-section";
//
import { getNavConfig } from "./config-navigation";
import NavDocs from "./NavDocs";
import NavAccount from "./NavAccount";
import NavToggleButton from "./NavToggleButton";
import { UserContext } from "../../../../contexts/UserContext";

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const { pathname } = useLocation();
  const { user } = useContext(UserContext);

  const isDesktop = useResponsive("up", "lg");

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // `compact` is the mobile drawer: same content, tighter, so more items fit on a
  // phone screen. Desktop's permanent drawer renders the roomy version.
  const renderContent = (compact: boolean) => (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      <Stack
        spacing={compact ? 1.5 : 3}
        sx={{
          pt: compact ? 2 : 3,
          pb: compact ? 1 : 2,
          px: compact ? 2 : 2.5,
          flexShrink: 0
        }}
      >
        <Logo />

        <NavAccount />
      </Stack>

      <NavSectionVertical data={getNavConfig(user?.email)} />

      <Box sx={{ flexGrow: 1 }} />

      <NavDocs />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD }
      }}
    >
      <NavToggleButton />

      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              zIndex: 0,
              width: NAV.W_DASHBOARD,
              bgcolor: "transparent",
              borderRightStyle: "dashed"
            }
          }}
        >
          {renderContent(false)}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
              // Scoped to this drawer, so the shared nav components keep their desktop
              // sizing in the permanent drawer above.
              "& .nav-account": {
                padding: (theme) => theme.spacing(1.25, 1.5)
              },
              "& .MuiListSubheader-root": {
                paddingTop: (theme) => theme.spacing(1.5),
                paddingBottom: (theme) => theme.spacing(0.5)
              },
              "& .MuiListItemButton-root": {
                height: 40,
                marginBottom: "2px"
              },
              "& .MuiList-root": {
                paddingLeft: (theme) => theme.spacing(1.5),
                paddingRight: (theme) => theme.spacing(1.5)
              }
            }
          }}
        >
          {renderContent(true)}
        </Drawer>
      )}
    </Box>
  );
}
